/**
 * Date utilities for handling different date formats from the backend
 *
 * With Jackson configured to serialize dates as ISO strings, the backend now sends:
 * - ISO strings: "2024-01-15T10:30:00.000Z" or "2024-01-15T10:30:00.000"
 * - Legacy array support maintained for backward compatibility during transition
 *
 * Usage examples:
 * - formatDateToISO("2024-01-15T10:30:00.000Z") → "2024-01-15"
 * - formatDateRelative("2024-01-15T10:30:00.000Z") → "Today", "Yesterday", "2 days ago", or "2024-01-15"
 * - formatDateTimeISO("2024-01-15T10:30:00.000Z") → "2024-01-15 10:30"
 */

/**
 * Converts various date formats to ISO date string
 * Handles:
 * - ISO strings from Jackson (e.g., "2024-01-15T10:30:00.000Z", "2024-01-15T10:30:00.000")
 * - Legacy Spring Boot LocalDateTime arrays (e.g., [2024, 1, 15, 10, 30, 0]) for backward compatibility
 * - Date objects
 */
export function formatDateToISO(dateInput: string | number[] | Date | null | undefined): string {
  if (!dateInput) {
    return 'Unknown date';
  }

  try {
    let date: Date;

    if (Array.isArray(dateInput)) {
      // Spring Boot LocalDateTime format: [year, month, day, hour, minute, second]
      // Note: Month in JavaScript Date is 0-indexed, but Spring sends 1-indexed
      const [year, month, day, hour = 0, minute = 0, second = 0] = dateInput;
      date = new Date(year, month - 1, day, hour, minute, second);
    } else if (typeof dateInput === 'string') {
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      throw new Error(`Unsupported date format: ${typeof dateInput}`);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }

    // Return ISO date string (YYYY-MM-DD format)
    return date.toISOString().split('T')[0];
  } catch (error) {
    console.warn('Failed to parse date:', dateInput, error);
    return 'Invalid date';
  }
}

/**
 * Formats date for relative display (e.g., "Today", "2 days ago", "2024-01-15")
 */
export function formatDateRelative(dateInput: string | number[] | Date | null | undefined): string {
  if (!dateInput) {
    return 'Unknown date';
  }

  try {
    let date: Date;

    if (Array.isArray(dateInput)) {
      // Spring Boot LocalDateTime format: [year, month, day, hour, minute, second]
      const [year, month, day, hour = 0, minute = 0, second = 0] = dateInput;
      date = new Date(year, month - 1, day, hour, minute, second);
    } else if (typeof dateInput === 'string') {
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      throw new Error(`Unsupported date format: ${typeof dateInput}`);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }

    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      // For older dates, return ISO format (YYYY-MM-DD)
      return date.toISOString().split('T')[0];
    }
  } catch (error) {
    console.warn('Failed to parse date:', dateInput, error);
    return 'Invalid date';
  }
}

/**
 * Formats date with time (ISO format: YYYY-MM-DD HH:MM)
 */
export function formatDateTimeISO(dateInput: string | number[] | Date | null | undefined): string {
  if (!dateInput) {
    return 'Unknown date';
  }

  try {
    let date: Date;

    if (Array.isArray(dateInput)) {
      // Spring Boot LocalDateTime format: [year, month, day, hour, minute, second]
      const [year, month, day, hour = 0, minute = 0, second = 0] = dateInput;
      date = new Date(year, month - 1, day, hour, minute, second);
    } else if (typeof dateInput === 'string') {
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      throw new Error(`Unsupported date format: ${typeof dateInput}`);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }

    // Return ISO format with time (YYYY-MM-DD HH:MM)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day} ${hour}:${minute}`;
  } catch (error) {
    console.warn('Failed to parse date:', dateInput, error);
    return 'Invalid date';
  }
}